<template>
	<div>
		<section class="home-hero">
			<div class="home-hero__backdrop">
				<div class="home-hero__media" :style="{backgroundImage: 'url(' + site.hero.background + ')'}"></div>
				<div class="home-hero__overlay"></div>
			</div>
			<div class="home-hero__content container reveal">
				<p class="home-hero__eyebrow">{{ site.hero.eyebrow }}</p>
				<h1 class="home-hero__title">
					<span v-for="line in site.hero.title" :key="line">{{ line }}</span>
				</h1>
				<div class="home-hero__copy">
					<p v-for="paragraph in site.hero.paragraphs" :key="paragraph">{{ paragraph }}</p>
				</div>
			</div>
		</section>

		<section class="values section-spacing dark-panel">
			<div class="container reveal">
				<div class="values__grid">
					<article v-for="item in site.values" :key="item.title" class="value-card">
						<div class="value-card__media">
							<img :src="item.image" :alt="item.title" loading="lazy" />
						</div>
						<h3>{{ item.title }}</h3>
					</article>
				</div>
			</div>
		</section>

		<!-- 创始人故事模块（暂隐藏，恢复时取消注释）
		<section class="story section-spacing">
			<div class="container story__layout reveal">
				<div class="story__image">
					<img :src="site.founder.image" :alt="site.founder.title" loading="lazy" />
				</div>
				<div class="story__content">
					<div class="section-title">
						<h2>{{ site.founder.title }}</h2>
					</div>
					<p v-for="paragraph in site.founder.paragraphs" :key="paragraph">{{ paragraph }}</p>
				</div>
			</div>
		</section>
		-->

		<section v-if="site.storyVideo.src" class="video-story section-spacing reveal">
			<div class="container">
				<div class="section-title section-title--center section-title--light">
					<h2>{{ site.storyVideo.title }}</h2>
				</div>
				<div class="video-story__frame">
					<video ref="brandVideo" class="video-story__player" :poster="site.storyVideo.poster" controls preload="metadata">
						<source :src="site.storyVideo.src" type="video/mp4" />
					</video>
					<button v-if="!videoStarted" class="video-story__play" type="button" @click="playVideo">
						<span></span>
					</button>
				</div>
			</div>
		</section>

		<section class="strength-preview section-spacing light-panel">
			<div class="container reveal">
				<div class="section-title section-title--center">
					<p>{{ site.strength.eyebrow }}</p>
					<h2>{{ site.strength.title }}</h2>
				</div>
				<p class="strength-preview__intro">{{ site.strength.intro }}</p>
				<div class="strength-preview__panel">
					<article v-for="item in site.strength.items" :key="item.title" class="strength-preview__item">
						<h3>{{ item.title }}</h3>
						<p>{{ item.body }}</p>
					</article>
				</div>
			</div>
		</section>

		<section class="interest-world section-spacing dark-panel">
			<div class="container interest-world__layout reveal">
				<div class="interest-world__header">
					<div class="section-title section-title--light">
						<p>{{ site.interests.eyebrow }}</p>
						<h2>{{ site.interests.title }}</h2>
					</div>
				</div>
				<div class="slider-shell">
					<div ref="interestSwiper" class="swiper home-swiper home-swiper--interest">
						<div class="swiper-wrapper">
							<div v-for="item in site.interests.items" :key="item.title" class="swiper-slide">
								<component :is="isExternal(item.href) ? 'a' : 'router-link'" v-bind="linkAttrs(item.href)" class="interest-card">
								<div class="interest-card__image">
									<img :src="item.image" :alt="item.title" loading="lazy" />
								</div>
								<h3>{{ item.title }}</h3>
								</component>
							</div>
						</div>
					</div>
					<button ref="interestPrev" style="border: 1px solid #fff;margin-top: 2px;" class="swiper-button-prev home-swiper__arrow home-swiper__arrow--prev" type="button" :aria-label="$t('common.previousSlide')"></button>
					<button ref="interestNext" style="border: 1px solid #fff;margin-top: 2px;" class="swiper-button-next home-swiper__arrow home-swiper__arrow--next" type="button" :aria-label="$t('common.nextSlide')"></button>
				</div>
			</div>
		</section>

		<section class="factory section-spacing dark-panel">
			<div class="container grid-two reveal">
				<div class="factory__text">
					<div class="section-title section-title--light" style="margin-bottom: 20px;">
						<h2>{{ site.factory.title }}</h2>
					</div>
					<article v-for="(location, index) in site.factory.locations" :key="location.title || 'factory-location-' + index" class="factory__location">
						<h3 v-if="location.title">{{ location.title }}</h3>
						<p>
							<strong>{{ $t("common.country") }}:</strong> {{ location.country }}
						</p>
						<p>
							<strong>{{ $t("common.address") }}:</strong> {{ location.address }}
						</p>
						<p>
							<strong>{{ $t("common.phone") }}:</strong> {{ location.phone }}
						</p>
						<p>
							<strong>{{ $t("common.email") }}:</strong> {{ location.email }}
						</p>
					</article>
				</div>
				<div class="factory__media">
					<img :src="site.factory.image" :alt="site.factory.title" loading="lazy" />
				</div>
			</div>
		</section>

		<section class="references section-spacing dark-panel">
			<div class="container reveal">
				<div class="section-title section-title--light">
					<p>{{ site.references.eyebrow }}</p>
					<h2>{{ site.references.title }}</h2>
				</div>
				<div class="slider-shell slider-shell--references">
					<div ref="referenceSwiper" class="swiper home-swiper home-swiper--references">
						<div class="swiper-wrapper">
							<div v-for="item in site.references.items" :key="item.title + item.image" class="swiper-slide">
								<div class="reference-card">
									<div class="reference-card__image">
										<img :src="item.image" :alt="item.title" loading="lazy" />
									</div>
									<h3>{{ item.title }}</h3>
								</div>
							</div>
						</div>
					</div>
					<button ref="referencePrev" class="swiper-button-prev home-swiper__arrow home-swiper__arrow--prev" type="button" :aria-label="$t('common.previousSlide')"></button>
					<button ref="referenceNext" class="swiper-button-next home-swiper__arrow home-swiper__arrow--next" type="button" :aria-label="$t('common.nextSlide')"></button>
				</div>
			</div>
		</section>

		<!-- <section class="certifications section-spacing light-panel">
      <div class="container reveal">
        <div class="section-title section-title--center">
          <p>{{ site.certifications.eyebrow }}</p>
          <h2>{{ site.certifications.title }}</h2>
        </div>
        <div class="certifications__grid">
          <article
            v-for="item in site.certifications.items"
            :key="item.title"
            class="certification-card"
          >
            <div class="certification-card__image">
              <img :src="item.image" :alt="item.title" loading="lazy">
            </div>
            <h3>{{ item.title }}</h3>
          </article>
        </div>
      </div>
    </section> -->

		<section class="contact-preview section-spacing">
			<div class="contact-preview__layout reveal">
				<div class="contact-preview__visual">
					<img :src="site.contact.image" :alt="$t('common.contactStrongova')" loading="lazy" />
				</div>
				<div class="contact-preview__form-wrap">
					<form class="contact-preview__form" @submit.prevent="submitForm">
						<div class="contact-preview__row">
							<label>
								<span>{{ $t("common.email") }}</span>
								<input v-model.trim="form.email" type="email" required />
							</label>
						</div>
						<div class="contact-preview__row contact-preview__row--split">
							<label>
								<span>{{ $t("common.zipCode") }}</span>
								<input v-model.trim="form.zip" type="text" required />
							</label>
							<label>
								<span>{{ $t("common.country") }}</span>
								<select v-model="form.country" required>
									<option disabled value="">{{ $t("common.pleaseSelect") }}</option>
									<option v-for="country in site.contact.countries" :key="country" :value="country">{{ country }}</option>
								</select>
							</label>
						</div>
						<div class="contact-preview__row contact-preview__row--split">
							<label>
								<span>{{ $t("common.phone") }}</span>
								<input v-model.trim="form.phone" type="text" required />
							</label>
							<label>
								<span>{{ $t("common.name") }}</span>
								<input v-model.trim="form.name" type="text" required />
							</label>
						</div>
						<div class="contact-preview__row">
							<label>
								<span>{{ $t("common.messageToUs") }}</span>
								<textarea v-model.trim="form.message" rows="5"></textarea>
							</label>
						</div>
						<div class="contact-preview__actions">
							<button type="submit" :disabled="submitting">{{ submitting ? $t("common.submitting") : $t("common.submit") }}</button>
							<router-link to="/contact" class="contact-preview__link">{{ $t("contact.openFullPage") }}</router-link>
						</div>
						<p v-if="submitMessage" class="contact-preview__feedback">{{ submitMessage }}</p>
					</form>
				</div>
			</div>
		</section>
	</div>
</template>

<script>
	import Swiper from "swiper";
	import Autoplay from "swiper/modules/autoplay.mjs";
	import Navigation from "swiper/modules/navigation.mjs";
	import "swiper/swiper.css";
	import "swiper/modules/navigation.css";
	import site from "../mock/strongova";
	import {fetchPublicCases, fetchPublicPageSections, normalizeCase, resolveAssetUrl, submitInquiry} from "../api/public";

	export default {
		name: "HomePage",
		data() {
			return {
				site,
				videoStarted: false,
				submitMessage: "",
				submitting: false,
				interestIndex: 0,
				referenceIndex: 0,
				interestSwiper: null,
				referenceSwiper: null,
				form: {
					email: "",
					zip: "",
					country: "",
					phone: "",
					name: "",
					message: "",
				},
			};
		},
		created() {
			this.loadPageSections();
			this.loadCases();
		},
		mounted() {
			this.initHomeSwipers();
		},
		beforeDestroy() {
			this.destroyHomeSwipers();
		},
		computed: {
			interestMaxIndex() {
				return Math.max(this.site.interests.items.length - 3, 0);
			},
			referenceMaxIndex() {
				return Math.max(this.site.references.items.length - 3, 0);
			},
		},
		methods: {
			loadPageSections() {
				fetchPublicPageSections({pageKey: "home"})
					.then((sections) => {
						if (Array.isArray(sections) && sections.length) {
							this.applyPageSections(sections);
						}
					})
					.catch(() => {});
			},
			loadCases() {
				fetchPublicCases()
					.then((cases) => {
						if (Array.isArray(cases) && cases.length) {
							const fallbackImage = this.site.references.items[0] && this.site.references.items[0].image;
							this.site.references.items = cases.map((item) => normalizeCase(item, fallbackImage));
							this.referenceIndex = this.clamp(this.referenceIndex, 0, this.referenceMaxIndex);
							this.initHomeSwipers();
						}
					})
					.catch(() => {});
			},
			applyPageSections(sections) {
				const hero = sections.find((item) => item.sectionKey === "hero");
				// const founder = sections.find((item) => item.sectionKey === "intro");
				const strength = sections.find((item) => item.sectionKey === "strength");
				const interests = sections.find((item) => item.sectionKey === "interests");
				const video = sections.find((item) => item.sectionKey === "video");
				const factory = sections.find((item) => item.sectionKey === "factory");
				const contact = sections.find((item) => item.sectionKey === "contact");

				if (hero) {
					this.site.hero.eyebrow = hero.title || this.site.hero.eyebrow;
					this.site.hero.background = resolveAssetUrl(hero.image, this.site.hero.background);
					if (hero.description) {
						const lines = hero.description
							.split("\n")
							.map((line) => line.trim())
							.filter(Boolean);
						if (lines.length)
							this.site.hero.title = lines[0]
								.split("|")
								.map((line) => line.trim())
								.filter(Boolean);
						if (lines.length > 1) this.site.hero.paragraphs = lines.slice(1);
					}
				}

				// 创始人故事（intro）— 与上方模板注释同步恢复
				// if (founder) {
				// 	this.site.founder.title = founder.title || this.site.founder.title;
				// 	this.site.founder.image = resolveAssetUrl(founder.image, this.site.founder.image);
				// 	if (founder.description) {
				// 		this.site.founder.paragraphs = founder.description
				// 			.split("\n")
				// 			.map((line) => line.trim())
				// 			.filter(Boolean);
				// 	}
				// }

				if (strength) {
					this.site.strength.title = strength.title || this.site.strength.title;
					this.site.strength.intro = strength.description || this.site.strength.intro;
					if (Array.isArray(strength.imagesJson) && strength.imagesJson.length) {
						this.site.strength.items = strength.imagesJson
							.map((item) => ({
								title: item.title || "",
								body: item.description || item.body || "",
							}))
							.filter((item) => item.title || item.body);
					}
				}

				if (interests) {
					this.site.interests.title = interests.title || this.site.interests.title;
					this.site.interests.eyebrow = interests.description || this.site.interests.eyebrow;
					if (Array.isArray(interests.imagesJson) && interests.imagesJson.length) {
						this.site.interests.items = interests.imagesJson
							.map((item) => ({
								title: item.title || "",
								image: resolveAssetUrl(item.image),
								href: item.href || item.to || "/",
							}))
							.filter((item) => item.title && item.image);
						this.initHomeSwipers();
					}
				}

				if (video) {
					this.site.storyVideo.title = video.title || this.site.storyVideo.title;
					this.site.storyVideo.poster = resolveAssetUrl(video.image, this.site.storyVideo.poster);
					if (video.description) this.site.storyVideo.src = video.description;
				}

				if (factory) {
					this.site.factory.title = factory.title || this.site.factory.title;
					this.site.factory.image = resolveAssetUrl(factory.image, this.site.factory.image);
				}

				if (contact) {
					this.site.contact.image = resolveAssetUrl(contact.image, this.site.contact.image);
				}
			},
			isExternal(href) {
				return /^https?:\/\//.test(href);
			},
			linkAttrs(href) {
				return this.isExternal(href) ? {href} : {to: href};
			},
			clamp(value, min, max) {
				return Math.min(Math.max(value, min), max);
			},
			slideInterest(direction) {
				if (!this.interestSwiper) return;
				if (direction > 0) this.interestSwiper.slideNext();
				else this.interestSwiper.slidePrev();
			},
			slideReference(direction) {
				if (!this.referenceSwiper) return;
				if (direction > 0) this.referenceSwiper.slideNext();
				else this.referenceSwiper.slidePrev();
			},
			initHomeSwipers() {
				this.$nextTick(() => {
					this.destroyHomeSwipers();
					if (this.$refs.interestSwiper && this.site.interests.items.length > 1) {
						this.interestSwiper = this.createHomeSwiper(this.$refs.interestSwiper, this.$refs.interestPrev, this.$refs.interestNext, false);
					}
					if (this.$refs.referenceSwiper && this.site.references.items.length > 1) {
						this.referenceSwiper = this.createHomeSwiper(this.$refs.referenceSwiper, this.$refs.referencePrev, this.$refs.referenceNext, true);
					}
				});
			},
			createHomeSwiper(el, prevEl, nextEl, isReference) {
				return new Swiper(el, {
					modules: isReference ? [Navigation, Autoplay] : [Navigation],
					speed: 720,
					spaceBetween: isReference ? 24 : 22,
					grabCursor: true,
					watchOverflow: true,
					watchSlidesProgress: true,
					slidesPerView: 1,
					navigation: {
						prevEl,
						nextEl,
					},
					...(isReference
						? {
								autoplay: {
									delay: 5200,
									disableOnInteraction: false,
									pauseOnMouseEnter: true,
								},
							}
						: {}),
					breakpoints: {
						768: {
							slidesPerView: isReference ? 2 : 1.45,
						},
						1100: {
							slidesPerView: isReference ? 3 : 2.35,
						},
						1440: {
							slidesPerView: isReference ? 3 : 2.9,
						},
					},
					on: {
						slideChange: (swiper) => {
							if (isReference) this.referenceIndex = swiper.activeIndex;
							else this.interestIndex = swiper.activeIndex;
						},
					},
				});
			},
			destroyHomeSwipers() {
				if (this.interestSwiper) {
					this.interestSwiper.destroy(true, true);
					this.interestSwiper = null;
				}
				if (this.referenceSwiper) {
					this.referenceSwiper.destroy(true, true);
					this.referenceSwiper = null;
				}
			},
			playVideo() {
				this.videoStarted = true;
				this.$refs.brandVideo.play();
			},
			submitForm() {
				if (this.submitting) return;
				this.submitting = true;
				this.submitMessage = "";

				submitInquiry({
					source: "home",
					firstName: this.form.name,
					email: this.form.email,
					zip: this.form.zip,
					country: this.form.country,
					phone: this.form.phone,
					message: this.form.message,
					requestType: "home_contact",
				})
					.then(() => {
						this.submitMessage = `Thank you, ${this.form.name || "friend"}. Your message has been submitted.`;
						this.form = {
							email: "",
							zip: "",
							country: "",
							phone: "",
							name: "",
							message: "",
						};
					})
					.catch(() => {
						this.submitMessage = "Sorry, the message could not be submitted. Please try again later.";
					})
					.then(() => {
						this.submitting = false;
					});
			},
		},
	};
</script>

<style>
	.home-hero {
		position: relative;
		min-height: 100vh;
		padding-top: 60px;
		overflow: hidden;
		background: #0a0a0a;
	}

	.home-hero__backdrop {
		position: absolute;
		inset: 0;
		z-index: 0;
		overflow: hidden;
	}

	.home-hero__media,
	.home-hero__overlay {
		position: absolute;
		inset: 0;
	}

	.home-hero__media {
		background-position: center;
		background-size: cover;
		transform: scale(1.03);
		animation: heroDrift 18s ease-out both;
		will-change: transform;
	}

	.home-hero__overlay {
		z-index: 1;
		background: radial-gradient(circle at 72% 36%, rgba(255, 255, 255, 0.1), transparent 26%), linear-gradient(90deg, rgba(0, 0, 0, 0.84) 0%, rgba(0, 0, 0, 0.34) 42%, rgba(0, 0, 0, 0.14) 100%), linear-gradient(180deg, rgba(0, 0, 0, 0.42) 0%, rgba(0, 0, 0, 0.22) 18%, rgba(0, 0, 0, 0.68) 82%, rgba(0, 0, 0, 0.88) 100%);
	}

	.home-hero__content {
		position: relative;
		z-index: 2;
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		min-height: calc(100vh - 60px);
		padding: 62px 0 42px;
	}

	.home-hero__eyebrow {
		margin: 0;
		font-size: 15px;
		font-weight: 500;
		letter-spacing: 0.16em;
		text-transform: uppercase;
		color: rgba(246, 241, 234, 0.82);
	}

	.home-hero__title {
		margin: 40px 0 28px;
		font-size: clamp(76px, 9vw, 136px);
		line-height: 0.92;
		font-weight: 300;
		letter-spacing: 0.03em;
	}

	.home-hero__title span {
		display: block;
	}

	.home-hero__copy {
		max-width: 980px;
		padding: 24px 0 0;
		border-top: 1px solid rgba(255, 255, 255, 0.16);
		font-size: 17px;
		line-height: 1.8;
		color: var(--muted);
	}

	.home-hero__copy p {
		margin: 0 0 14px;
	}

	.values__grid,
	.certifications__grid {
		display: grid;
		gap: 22px;
	}

	.values__grid {
		grid-template-columns: repeat(4, minmax(0, 1fr));
	}

	.value-card {
		position: relative;
		overflow: hidden;
		background: #141414;
		border-radius: 10px;
	}

	.value-card::after {
		content: "";
		position: absolute;
		inset: 0;
		border: 1px solid rgba(255, 255, 255, 0.1);
		pointer-events: none;
	}

	.value-card__media,
	.story__image,
	.interest-card__image,
	.reference-card__image,
	.factory__media img,
	.certification-card__image {
		overflow: hidden;
	}

	.value-card__media {
		aspect-ratio: 3 / 4;
	}

	.value-card__media img,
	.story__image img,
	.video-story__player,
	.interest-card__image img,
	.factory__media img,
	.reference-card__image img,
	.certification-card__image img {
		width: 100%;
		height: 100%;
		display: block;
		object-fit: cover;
	}

	.value-card__media img,
	.interest-card__image img {
		transition: transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
	}

	.value-card:hover .value-card__media img,
	.interest-card:hover .interest-card__image img {
		transform: scale(1.06);
	}

	.value-card h3 {
		margin: 0;
		padding: 18px 12px 0;
		font-size: 20px;
		font-weight: 400;
		text-align: center;
		letter-spacing: 0.04em;
	}

	.story {
		background: var(--panel-soft);
		color: var(--dark-text);
	}

	.story__layout {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 48px;
		align-items: start;
	}

	.contact-preview__layout {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 0;
		align-items: stretch;
	}

	.interest-world__layout {
		display: grid;
		grid-template-columns: minmax(240px, 0.34fr) minmax(0, 1fr);
		gap: clamp(44px, 5vw, 88px);
		align-items: center;
	}

	.story__image {
		aspect-ratio: 4 / 3;
	}

	.story__content p,
	.factory__location p,
	.strength-preview__intro,
	.strength-preview__item p {
		margin: 0 0 16px;
		font-size: 16px;
		line-height: 1.85;
		color: rgba(23, 20, 17, 0.78);
	}

	.video-story {
		background: #0f0f0f;
	}

	.video-story__frame {
		position: relative;
		overflow: hidden;
		aspect-ratio: 16 / 9;
		border-radius: 12px;
		box-shadow: 0 22px 48px rgba(0, 0, 0, 0.22);
		margin-top: 20px;
	}

	.video-story__player {
		background: #060606;
	}

	.video-story__play {
		position: absolute;
		left: 50%;
		top: 50%;
		width: 98px;
		height: 98px;
		border-radius: 50%;
		border: 0;
		background: rgba(255, 255, 255, 0.92);
		transform: translate(-50%, -50%);
		cursor: pointer;
		box-shadow: 0 18px 36px rgba(0, 0, 0, 0.22);
	}

	.video-story__play span {
		position: absolute;
		left: 50%;
		top: 50%;
		width: 0;
		height: 0;
		border-top: 14px solid transparent;
		border-bottom: 14px solid transparent;
		border-left: 22px solid #111;
		transform: translate(-35%, -50%);
	}

	.strength-preview__intro {
		max-width: 920px;
		margin: 0 auto 24px;
		text-align: center;
	}

	.strength-preview__panel {
		margin: 0 auto;
		max-width: 1020px;
		padding: 42px;
		border: 1px solid rgba(23, 20, 17, 0.12);
		border-radius: 12px;
		background: rgba(255, 255, 255, 0.48);
		box-shadow: 0 18px 36px rgba(23, 20, 17, 0.06);
	}

	.strength-preview__item + .strength-preview__item {
		margin-top: 28px;
	}

	.strength-preview__item h3,
	.factory__location h3,
	.interest-card h3,
	.reference-card h3,
	.certification-card h3 {
		margin: 0 0 10px;
		font-size: 24px;
		font-weight: 400;
	}

	.interest-world__header {
		position: relative;
		z-index: 4;
		align-self: center;
		padding: 28px 0 80px;
	}

	.interest-world__header .section-title h2 {
		max-width: 320px;
		font-size: clamp(44px, 4.6vw, 70px);
		line-height: 1.08;
	}

	.slider-shell {
		position: relative;
		min-width: 0;
		padding: 76px 0 0;
		--swiper-navigation-top-offset: 0;
	}

	.home-swiper {
		overflow: visible;
		padding: 4px 0 28px;
	}

	.home-swiper .swiper-slide {
		height: auto;
		transition: transform 0.5s ease, opacity 0.5s ease;
	}

	.home-swiper .swiper-slide:not(.swiper-slide-visible) {
		opacity: 0.42;
	}

	.home-swiper .swiper-slide-active {
		transform: translateY(-6px);
	}

	.home-swiper .swiper-slide:nth-child(2n) {
		transform: translateY(-72px);
	}

	.home-swiper .swiper-slide:nth-child(3n) {
		transform: translateY(34px);
	}

	.home-swiper .swiper-slide-active:nth-child(2n) {
		transform: translateY(-86px);
	}

	.home-swiper .swiper-slide-active:nth-child(3n) {
		transform: translateY(20px);
	}

	.home-swiper__arrow {
		position: absolute;
		top: 22px;
		z-index: 2;
		width: 96px;
		height: 42px;
		margin: 0;
		border: 0;
		border-radius: 0;
		background: transparent;
		box-shadow: none;
		color: #f5f3ef;
		cursor: pointer;
		transition: transform 0.25s ease, background 0.25s ease, border-color 0.25s ease, opacity 0.25s ease;
	}

	.home-swiper__arrow::before {
		/* content: "";
		position: absolute;
		left: 0;
		right: 0;
		top: 50%;
		height: 1px;
		background: rgba(245, 243, 239, 0.72);
		transform: translateY(-50%); */
	}

	.home-swiper__arrow::after {
		position: relative;
		z-index: 1;
		font-size: 12px;
		font-weight: 700;
		color: currentColor;
	}

	.home-swiper__arrow:hover {
		background: transparent;
		border-color: transparent;
		transform: translateX(4px);
	}

	.home-swiper__arrow--prev {
		right: 116px;
		left: auto;
	}

	.home-swiper__arrow--next {
		right: 0;
	}

	.home-swiper__arrow--prev:hover {
		transform: translateX(-4px);
	}

	.home-swiper__arrow.swiper-button-disabled {
		cursor: default;
		opacity: 0.2;
		transform: none;
	}

	.interest-world .slider-shell {
		overflow: hidden;
		margin-left: clamp(12px, 2vw, 34px);
		clip-path: inset(-140px -42vw -180px 0);
	}

	.interest-world .home-swiper {
		overflow: visible;
	}

	.interest-world .home-swiper__arrow {
		top: 18px;
	}

	.interest-world .home-swiper__arrow--prev {
		right: 118px;
		left: auto;
		

	}
	.interest-world .home-swiper__arrow--next {
		right: 0;
	}
	.swiper-navigation-icon{
		width: 50% !important;
		height: 50% !important;
	}

	.interest-card {
		display: block;
		color: inherit;
		height: 100%;
	}

	.interest-card__image {
		aspect-ratio: 4 / 5.35;
		background: #1a1a1a;
		box-shadow: 0 20px 38px rgba(0, 0, 0, 0.18);
	}

	.interest-card h3,
	.reference-card h3 {
		margin: 0;
		padding: 14px 4px 10px;
		border-top: 1px solid rgba(245, 243, 239, 0.44);
		border-bottom: 1px solid rgba(245, 243, 239, 0.22);
		font-size: 22px;
		line-height: 1.24;
		letter-spacing: 0.02em;
	}

	.factory__location + .factory__location {
		margin-top: 30px;
	}

	.factory__location p {
		color: rgba(245, 243, 239, 0.72);
	}

	.factory__location strong {
		color: var(--text);
		font-weight: 500;
	}

	.factory__media {
		position: relative;
		padding: 36px 0 0 36px;
	}

	.factory__media::before {
		content: "";
		position: absolute;
		inset: 0 0 72px 72px;
		border: 1px solid rgba(255, 255, 255, 0.16);
	}

	.factory__media img {
		position: relative;
		aspect-ratio: 4 / 3;
	}

	.slider-shell--references {
		margin-top: 28px;
		padding: 0 54px;
	}

	.slider-shell--references .home-swiper {
		overflow: hidden;
		padding: 4px 0 20px;
	}

	.slider-shell--references .swiper-slide,
	.slider-shell--references .swiper-slide:nth-child(2n),
	.slider-shell--references .swiper-slide:nth-child(3n),
	.slider-shell--references .swiper-slide-active,
	.slider-shell--references .swiper-slide-active:nth-child(2n),
	.slider-shell--references .swiper-slide-active:nth-child(3n) {
		transform: none;
	}

	.slider-shell--references .swiper-slide:not(.swiper-slide-visible) {
		opacity: 0.42;
	}

	.slider-shell--references .home-swiper__arrow {
		top: calc(50% - 28px);
		width: 46px;
		height: 46px;
		border: 1px solid rgba(255, 255, 255, 0.24);
		border-radius: 50%;
		background: rgba(17, 17, 16, 0.68);
		box-shadow: 0 18px 36px rgba(0, 0, 0, 0.2);
	}

	.slider-shell--references .home-swiper__arrow::before {
		display: none;
	}

	.slider-shell--references .home-swiper__arrow::after {
		font-size: 13px;
	}

	.slider-shell--references .home-swiper__arrow:hover {
		background: rgba(207, 176, 140, 0.24);
		border-color: rgba(207, 176, 140, 0.62);
		transform: translateY(-2px);
	}

	.slider-shell--references .home-swiper__arrow--prev {
		left: 0;
		right: auto;
	}

	.slider-shell--references .home-swiper__arrow--next {
		right: 0;
	}

	.reference-card__image {
		aspect-ratio: 4 / 3.2;
		background: #1a1a1a;
		box-shadow: 0 18px 34px rgba(0, 0, 0, 0.2);
	}

	.reference-card {
		display: block;
		color: inherit;
	}

	.reference-card h3 {
		margin-top: 14px;
		padding: 0 4px;
		border: 0;
	}

	.certifications__grid {
		grid-template-columns: repeat(6, minmax(0, 1fr));
	}

	.certification-card {
		padding: 18px 16px 22px;
		background: #fff;
		border: 1px solid var(--line-dark);
		text-align: center;
	}

	.certification-card__image {
		aspect-ratio: 1 / 1;
	}

	.contact-preview {
		padding-bottom: 0;
	}

	.contact-preview__visual {
		position: relative;
		min-height: 0;
		overflow: hidden;
	}

	.contact-preview__visual img {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.contact-preview__form-wrap {
		display: flex;
		flex-direction: column;
		height: 100%;
		background: #191919;
		padding: 64px;
		box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.02);
	}

	.contact-preview__form {
		max-width: 640px;
	}

	.contact-preview__row + .contact-preview__row {
		margin-top: 18px;
	}

	.contact-preview__row--split {
		display: grid;
		grid-template-columns: repeat(2, minmax(0, 1fr));
		gap: 18px;
	}

	.contact-preview__form label {
		display: block;
	}

	.contact-preview__form span {
		display: block;
		margin-bottom: 12px;
		font-size: 14px;
		color: rgba(245, 243, 239, 0.78);
		letter-spacing: 0.02em;
	}

	.contact-preview__form input,
	.contact-preview__form select,
	.contact-preview__form textarea {
		width: 100%;
		border: 1px solid rgba(255, 255, 255, 0.16);
		background: rgba(255, 255, 255, 0.03);
		color: var(--text);
		padding: 14px 16px;
		outline: none;
	}

	.contact-preview__form input:focus,
	.contact-preview__form select:focus,
	.contact-preview__form textarea:focus {
		border-color: rgba(207, 176, 140, 0.8);
		background: rgba(255, 255, 255, 0.05);
	}

	.contact-preview__actions {
		display: flex;
		align-items: center;
		gap: 18px;
		margin-top: 24px;
	}

	.contact-preview__actions button,
	.contact-preview__link {
		min-width: 120px;
		height: 46px;
		padding: 0 18px;
		border: 1px solid rgba(255, 255, 255, 0.18);
		background: rgba(255, 255, 255, 0.03);
		color: inherit;
		display: inline-flex;
		align-items: center;
		justify-content: center;
	}

	.contact-preview__feedback {
		margin-top: 18px;
		color: rgba(245, 243, 239, 0.74);
	}

	@media (max-width: 1280px) {
		.certifications__grid {
			grid-template-columns: repeat(3, minmax(0, 1fr));
		}
	}

	@media (max-width: 1100px) {
		.values__grid,
		.story__layout,
		.interest-world__layout,
		.contact-preview__layout {
			grid-template-columns: 1fr;
		}

		.interest-world__layout {
			gap: 28px;
		}

		.interest-world__header {
			padding: 0;
		}

		.interest-world .slider-shell {
			margin-left: 0;
			clip-path: none;
		}

		.home-swiper .swiper-slide,
		.home-swiper .swiper-slide:nth-child(2n),
		.home-swiper .swiper-slide:nth-child(3n),
		.home-swiper .swiper-slide-active:nth-child(2n),
		.home-swiper .swiper-slide-active:nth-child(3n) {
			transform: none;
		}

		.slider-shell--references {
			padding: 0 46px;
		}

		.certifications__grid {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}

		.contact-preview__visual {
			min-height: 320px;
		}

		.contact-preview__form-wrap {
			padding: 36px 24px;
		}
	}

	@media (max-width: 767px) {
		.home-hero {
			min-height: auto;
		}

		.home-hero__content {
			min-height: 620px;
			padding: 54px 0 34px;
		}

		.home-hero__eyebrow {
			font-size: 12px;
			letter-spacing: 0.12em;
		}

		.home-hero__title {
			margin: 26px 0 22px;
			font-size: 48px;
			line-height: 0.98;
		}

		.home-hero__copy {
			padding-top: 18px;
			font-size: 15px;
			line-height: 1.7;
		}

		.values__grid,
		.certifications__grid {
			grid-template-columns: 1fr;
			gap: 16px;
		}

		.value-card__media {
			aspect-ratio: 4 / 3;
		}

		.value-card h3 {
			padding: 14px 10px 16px;
			font-size: 18px;
		}

		.strength-preview__panel {
			padding: 24px 18px;
		}

		.video-story__play {
			width: 68px;
			height: 68px;
		}

		.video-story__play span {
			border-top-width: 10px;
			border-bottom-width: 10px;
			border-left-width: 16px;
		}

		.slider-shell,
		.slider-shell--references {
			padding: 52px 0 0;
		}

		.slider-shell--references {
			padding: 0 40px;
		}

		.home-swiper__arrow {
			top: 0;
			width: 72px;
			height: 34px;
		}

		.home-swiper__arrow::after {
			font-size: 10px;
		}

		.home-swiper__arrow--prev {
			right: 86px;
		}

		.slider-shell--references .home-swiper__arrow {
			top: calc(50% - 17px);
			width: 34px;
			height: 34px;
		}

		.slider-shell--references .home-swiper__arrow--prev {
			left: 0;
			right: auto;
		}

		.interest-world__header {
			padding-top: 0;
		}

		.contact-preview__visual {
			min-height: 260px;
		}

		.contact-preview__form-wrap {
			padding: 32px 16px;
		}

		.contact-preview__row--split {
			grid-template-columns: 1fr;
		}

		.contact-preview__actions {
			flex-direction: column;
			align-items: stretch;
		}

		.contact-preview__actions button,
		.contact-preview__link {
			width: 100%;
		}
	}

	@media (max-width: 480px) {
		.home-hero__content {
			min-height: 560px;
		}

		.home-hero__title {
			font-size: 40px;
		}
	}

	@keyframes heroDrift {
		from {
			transform: scale(1.08);
		}

		to {
			transform: scale(1.03);
		}
	}
</style>
